import {
    Box,
    Chip,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tabs,
    Tab,
} from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useState } from 'react';

interface NormalizedDependency {
    name: string;
    version: string;
    type: 'production' | 'development';
    manager: 'npm' | 'python' | 'nuget';
}

type PackageManager = 'all' | 'npm' | 'python' | 'nuget';

const useStyles = makeStyles(theme => ({
    tableContainer: {
        marginTop: theme.spacing(2),
    },
    packageName: {
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    version: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    managerBadge: {
        fontFamily: 'monospace',
        fontSize: '0.65rem',
        padding: '2px 6px',
        borderRadius: 3,
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    npmBadge: {
        backgroundColor: '#CC3534',
        color: '#fff',
    },
    pythonBadge: {
        backgroundColor: '#3776AB',
        color: '#fff',
    },
    nugetBadge: {
        backgroundColor: '#004880',
        color: '#fff',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
    },
    chip: {
        marginLeft: theme.spacing(1),
        height: 20,
        fontSize: '0.75rem',
    },
    emptyState: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    tableHeader: {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    tableCell: {
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    compactRow: {
        '&:last-child td': {
            borderBottom: 0,
        },
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
    },
    totalBox: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    totalCount: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: 1,
        color: theme.palette.primary.main,
    },
    totalLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    totalBreakdown: {
        marginLeft: 'auto',
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },
}));

export const PackageDependenciesCard = () => {
    const classes = useStyles();
    const { entity } = useEntity();
    const [activeTab, setActiveTab] = useState<PackageManager>('all');

    // Extract package info from annotations
    const annotations = entity.metadata.annotations || {};

    // Parse unified dependencies
    let allDependencies: NormalizedDependency[] = [];
    try {
        if (annotations['dependencies/all']) {
            allDependencies = JSON.parse(annotations['dependencies/all']);
        }
    } catch (error) {
        console.error('Failed to parse unified dependencies:', error);
    }

    // Parse dependency counts
    let dependencyCounts: { [manager: string]: { production: number; development: number } } = {
        npm: { production: 0, development: 0 },
        python: { production: 0, development: 0 },
        nuget: { production: 0, development: 0 },
    };
    try {
        if (annotations['dependencies/counts']) {
            dependencyCounts = JSON.parse(annotations['dependencies/counts']);
        }
    } catch (error) {
        console.error('Failed to parse dependency counts:', error);
    }

    // Filter dependencies based on active tab
    const filteredDependencies = activeTab === 'all'
        ? allDependencies
        : allDependencies.filter(dep => dep.manager === activeTab);

    // Separate production and dev dependencies
    const productionDeps = filteredDependencies.filter(dep => dep.type === 'production');
    const developmentDeps = filteredDependencies.filter(dep => dep.type === 'development');

    // Calculate total counts
    const totalProduction = allDependencies.filter(dep => dep.type === 'production').length;
    const totalDevelopment = allDependencies.filter(dep => dep.type === 'development').length;

    // Get manager badge class
    const getManagerBadgeClass = (manager: string) => {
        switch (manager) {
            case 'npm': return classes.npmBadge;
            case 'python': return classes.pythonBadge;
            case 'nuget': return classes.nugetBadge;
            default: return '';
        }
    };

    // If no dependencies found, show a message
    if (allDependencies.length === 0) {
        return (
            <InfoCard title="Package Dependencies">
                <div className={classes.emptyState}>
                    <Typography variant="body2">
                        No dependency files found in this component's repository
                    </Typography>
                    <Typography variant="caption" style={{ marginTop: 16, display: 'block' }}>
                        Supports: package.json (npm), requirements.txt (Python), *.csproj & packages.config (NuGet)
                    </Typography>
                </div>
            </InfoCard>
        );
    }

    // Determine available managers
    const availableManagers = ['all', ...Object.keys(dependencyCounts).filter(
        manager => dependencyCounts[manager].production > 0 || dependencyCounts[manager].development > 0
    )] as PackageManager[];

    const renderDependencyTable = (deps: NormalizedDependency[], title: string, isDev: boolean = false) => {
        if (deps.length === 0) return null;

        const displayLimit = isDev ? 15 : 50;
        const displayedDeps = deps.sort((a, b) => a.name.localeCompare(b.name)).slice(0, displayLimit);

        return (
            <>
                <Box className={classes.sectionHeader}>
                    <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 500 }}>
                        {title}
                    </Typography>
                    <Chip
                        label={deps.length}
                        size="small"
                        className={classes.chip}
                        color={isDev ? undefined : 'primary'}
                    />
                </Box>
                <TableContainer className={classes.tableContainer}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeader}>Package Name</TableCell>
                                <TableCell className={classes.tableHeader} align="center">Manager</TableCell>
                                <TableCell className={classes.tableHeader} align="right">Version</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedDeps.map((dep, index) => (
                                <TableRow key={`${dep.manager}-${dep.name}-${index}`} className={classes.compactRow}>
                                    <TableCell className={classes.tableCell}>
                                        <span className={classes.packageName}>{dep.name}</span>
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="center">
                                        <span className={`${classes.managerBadge} ${getManagerBadgeClass(dep.manager)}`}>
                                            {dep.manager}
                                        </span>
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        <span className={classes.version}>{dep.version}</span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {deps.length > displayLimit && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{
                            marginLeft: 16,
                            marginTop: 8,
                            display: 'block',
                            fontStyle: 'italic'
                        }}
                    >
                        ... and {deps.length - displayLimit} more {title.toLowerCase()}
                    </Typography>
                )}
            </>
        );
    };

    const totalDeps = allDependencies.length;

    return (
        <InfoCard
            title="Package Dependencies"
        >
            <Box className={classes.totalBox}>
                <Typography className={classes.totalCount}>{totalDeps}</Typography>
                <Typography className={classes.totalLabel}>total dependencies</Typography>
                <Typography className={classes.totalBreakdown}>
                    {totalProduction} production &nbsp;·&nbsp; {totalDevelopment} development
                </Typography>
            </Box>
            {availableManagers.length > 1 && (
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label={`All (${allDependencies.length})`} value="all" />
                    {availableManagers.includes('npm') && (
                        <Tab
                            label={`NPM (${dependencyCounts.npm.production + dependencyCounts.npm.development})`}
                            value="npm"
                        />
                    )}
                    {availableManagers.includes('python') && (
                        <Tab
                            label={`Python (${dependencyCounts.python.production + dependencyCounts.python.development})`}
                            value="python"
                        />
                    )}
                    {availableManagers.includes('nuget') && (
                        <Tab
                            label={`NuGet (${dependencyCounts.nuget.production + dependencyCounts.nuget.development})`}
                            value="nuget"
                        />
                    )}
                </Tabs>
            )}

            {renderDependencyTable(productionDeps, 'Production Dependencies')}
            {renderDependencyTable(developmentDeps, 'Development Dependencies', true)}

            {filteredDependencies.length === 0 && (
                <div className={classes.emptyState}>
                    <Typography variant="body2">
                        No {activeTab !== 'all' ? activeTab.toUpperCase() : ''} dependencies found
                    </Typography>
                </div>
            )}
        </InfoCard>
    );
};
